import { plasterNav, paintingNav, additionalNav } from "@/data/siteNavigation";
import { Phone, Mail, MapPin, Facebook, Instagram, Youtube, Shield, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import atpLogoAsset from "@/assets/atp-logo-dark.png.asset.json";
import { BUSINESS_INFO } from "@/data/businessInfo";
import { footerGroups, SHOP_URL } from "@/data/siteNavigation";
import { googleReviewSummary } from "@/data/googleReviews";

const atpLogo = atpLogoAsset.url;

const specialtyLinks = plasterNav.items;

const servicesLinks = paintingNav.items;
const additionalLinks = additionalNav.items;

const companyLinks = [
  { name: "Projects", href: "/projects/" },
  { name: "About Us", href: "/about/" },
  { name: "Service Areas", href: "/service-areas/" },
  { name: "Contact", href: "/contact/" },
];

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-secondary text-secondary-foreground">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-14 md:py-20">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-[1.3fr_1fr_1fr_1.2fr]">
          {/* Brand */}
          <div>
            <img
              src={atpLogo}
              alt="All Time Paint & Plaster Co."
              width={160}
              height={160}
              className="h-20 w-auto mb-6 bg-background p-2"
              loading="lazy"
            />
            <p className="text-secondary-foreground/70 text-sm leading-relaxed max-w-sm">
              Venetian plaster, decorative lime plaster, limewash and microcement for North Jersey spaces, with professional painting to complete your project. Based in Wayne.
            </p>
            <div className="flex gap-3 mt-6">
              {[
                { href: BUSINESS_INFO.social.facebook, label: "Facebook", Icon: Facebook },
                { href: BUSINESS_INFO.social.instagram, label: "Instagram", Icon: Instagram },
                { href: BUSINESS_INFO.social.youtube, label: "YouTube", Icon: Youtube },
              ].map(({ href, label, Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${label} — All Time Paint & Plaster Co.`}
                  className="w-9 h-9 border border-secondary-foreground/20 flex items-center justify-center text-secondary-foreground/70 hover:text-clay hover:border-clay transition-colors"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-clay-light mb-5">Specialty Finishes</p>
            <nav className="flex flex-col gap-3" aria-label="Footer specialty services navigation">
              {specialtyLinks.map((link) => (
                <Link key={link.href} to={link.href} className="text-sm text-secondary-foreground/70 hover:text-clay-light transition-colors">
                  {link.name}
                </Link>
              ))}
            </nav>
            <p className="text-[10px] uppercase tracking-[0.28em] text-clay-light mt-8 mb-5">Painting</p>
            <nav className="flex flex-col gap-3" aria-label="Footer painting services navigation">
              {servicesLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-sm text-secondary-foreground/70 hover:text-clay-light transition-colors"
                >
                  {link.name}
                </Link>
              ))}
            </nav>
            <p className="text-[10px] uppercase tracking-[0.28em] text-clay-light mt-8 mb-4">Additional Services</p>
            <nav className="flex flex-col gap-2" aria-label="Footer additional services navigation">
              {additionalLinks.map((link) => (
                <Link key={link.href} to={link.href} className="text-xs text-secondary-foreground/60 hover:text-clay-light transition-colors">
                  {link.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* Company */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-clay-light mb-5">Company</p>
            <nav className="flex flex-col gap-3" aria-label="Footer company navigation">
              <Link
                to={companyLinks[0].href}
                className="text-sm text-secondary-foreground/70 hover:text-clay-light transition-colors"
              >
                {companyLinks[0].name}
              </Link>
              <Link
                to={companyLinks[1].href}
                className="text-sm text-secondary-foreground/70 hover:text-clay-light transition-colors"
              >
                {companyLinks[1].name}
              </Link>
              <Link
                to={companyLinks[2].href}
                className="text-sm text-secondary-foreground/70 hover:text-clay-light transition-colors"
              >
                {companyLinks[2].name}
              </Link>
              <a
                href={googleReviewSummary.profileUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-secondary-foreground/70 hover:text-clay-light transition-colors"
              >
                Google Reviews
              </a>
              <Link
                to={companyLinks[3].href}
                className="text-sm font-semibold text-secondary-foreground hover:text-clay-light transition-colors"
              >
                {companyLinks[3].name}
              </Link>
              <a
                href={SHOP_URL}
                className="text-sm text-secondary-foreground/70 hover:text-clay-light transition-colors"
              >
                Shop Materials
              </a>
              <Link to="/careers/" className="text-sm font-semibold text-secondary-foreground hover:text-clay-light transition-colors">Careers — We’re Hiring</Link>
            </nav>
          </div>

          {/* Contact */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.28em] text-clay-light mb-5">Contact</p>
            <div className="space-y-4 text-sm">
              <a
                href={BUSINESS_INFO.phoneLink}
                className="flex items-center gap-3 font-semibold hover:text-clay-light transition-colors"
                aria-label={`Call All Time Paint & Plaster Co. at ${BUSINESS_INFO.phone}`}
              >
                <Phone className="w-4 h-4 text-clay-light" />
                {BUSINESS_INFO.phone}
              </a>
              <a
                href={BUSINESS_INFO.emailLink}
                className="flex items-center gap-3 text-secondary-foreground/70 hover:text-clay-light transition-colors"
              >
                <Mail className="w-4 h-4 text-clay-light" />
                {BUSINESS_INFO.email}
              </a>
              <address className="flex items-start gap-3 not-italic text-secondary-foreground/70">
                <MapPin className="w-4 h-4 text-clay-light mt-0.5 shrink-0" />
                <span>
                  {BUSINESS_INFO.address.street}
                  <br />
                  {BUSINESS_INFO.address.city}, {BUSINESS_INFO.address.state} {BUSINESS_INFO.address.zip}
                </span>
              </address>
              <p className="text-secondary-foreground/60 leading-relaxed">
                Based in Wayne, NJ • Serving Bergen, Passaic, Morris, Essex, Hudson &amp;
                surrounding North Jersey communities.
              </p>
              <Link
                to="/contact/?source=footer"
                className="inline-flex w-full sm:w-auto items-center justify-center bg-clay hover:bg-clay-dark text-primary-foreground px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.16em] transition-colors"
              >
                Request a Free Estimate
              </Link>
            </div>
          </div>
        </div>

        {/* Full site link map — collapsed by default */}
        <details className="mt-14 pt-10 border-t border-secondary-foreground/10 group">
          <summary className="cursor-pointer list-none flex items-center gap-3 text-[10px] uppercase tracking-[0.28em] text-clay-light hover:text-secondary-foreground transition-colors">
            <ChevronDown className="w-4 h-4 transition-transform group-open:rotate-180" aria-hidden="true" />
            View Complete Site Directory
          </summary>
          <nav
          className="mt-10 grid gap-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6"
          aria-label="Site directory"
        >
          {footerGroups.map((group) => (
            <div key={group.title}>
              <p className="text-[10px] uppercase tracking-[0.28em] text-clay-light mb-4">
                {group.title}
              </p>
              <ul className="space-y-2.5">
                {group.items.map((item) => (
                  <li key={`${group.title}-${item.name}`}>
                    <Link
                      to={item.href}
                      className="text-sm text-secondary-foreground/70 hover:text-clay-light transition-colors"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          </nav>
        </details>

        <div className="mt-14 pt-6 border-t border-secondary-foreground/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="flex items-center gap-2 text-xs text-secondary-foreground/60">
            <Shield className="w-4 h-4 text-clay-light" />
            {BUSINESS_INFO.license.fullText} • NJ Licensed &amp; Insured
          </p>
          <div className="flex items-center gap-5 text-xs text-secondary-foreground/50">
            <span>© {currentYear} {BUSINESS_INFO.name}</span>
            <Link to="/privacy-policy/" className="hover:text-clay-light transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service/" className="hover:text-clay-light transition-colors">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
